import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Award, RefreshCw, Ban, Download, X, ShieldCheck, ShieldAlert } from "lucide-react";
import { api, API_BASE, errorToMessage, getStoredAuth } from "@/lib/apiClient";
import { canWarrantyVoid, canWarrantyWrite, useAuth } from "@/lib/auth";
import { WARR } from "@/constants/testIds";

const STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-800",
  EXPIRED: "bg-slate-200 text-slate-600",
  VOIDED: "bg-rose-100 text-rose-800",
};
const TYPE_COLORS = {
  STANDARD: "bg-sky-100 text-sky-800",
  EXTENDED: "bg-indigo-100 text-indigo-800",
  RENEWAL: "bg-teal-100 text-teal-800",
};

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export default function WarrantyDetailPage() {
  const { warranty_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const [renewPlanId, setRenewPlanId] = useState("");
  const [renewAmount, setRenewAmount] = useState("");
  const [voidReason, setVoidReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [d, plansResp] = await Promise.all([
        api.get(`/warranties/${warranty_id}`),
        api.get("/warranties/plans"),
      ]);
      setDetail(d.data);
      setPlans(plansResp.data);
      if (d.data.passport_id) {
        const h = await api.get(`/warranties/history/${d.data.passport_id}`);
        setHistory(h.data);
      }
    } catch (e) {
      setErr(errorToMessage(e, "Failed to load warranty"));
    } finally { setLoading(false); }
  }, [warranty_id]);

  useEffect(() => { load(); }, [load]);

  const closeModal = () => {
    setModal(null); setActionErr(null);
    setRenewPlanId(""); setRenewAmount(""); setVoidReason("");
  };

  const runAction = async (label, fn) => {
    setBusy(true); setActionErr(null);
    try {
      await fn(); await load(); closeModal();
    } catch (e) {
      setActionErr(errorToMessage(e, `${label} failed`));
    } finally { setBusy(false); }
  };

  const issueCert = () =>
    runAction("Certificate", async () => {
      await api.post(`/warranties/${warranty_id}/certificate`);
    });

  const submitRenew = () =>
    runAction("Renew", async () => {
      const body = {};
      if (renewPlanId) body.warranty_plan_id = renewPlanId;
      if (renewAmount) body.amount_paid = renewAmount;
      const { data } = await api.post(`/warranties/${warranty_id}/renew`, body);
      navigate(`/admin/warranties/${data.id}`);
    });

  const submitVoid = () =>
    runAction("Void", async () => {
      if (!voidReason.trim()) throw new Error("Reason required");
      await api.post(`/warranties/${warranty_id}/void`,
        { reason: voidReason.trim() });
    });

  const downloadCert = async () => {
    if (!detail?.certificate_no) return;
    const stored = getStoredAuth();
    const token = stored?.access_token;
    try {
      const resp = await fetch(
        `${API_BASE}/warranties/certificates/${detail.certificate_no}.pdf`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${detail.certificate_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setActionErr(errorToMessage(e, "Download failed"));
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (err) return <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>;
  if (!detail) return null;

  const canWrite = canWarrantyWrite(user);
  const canVoid = canWarrantyVoid(user);
  const isActive = detail.effective_status === "ACTIVE";
  const isVoided = detail.effective_status === "VOIDED";

  return (
    <div className="space-y-6 pb-10" data-testid="warranty-detail-page">
      <div className="flex items-baseline justify-between">
        <div>
          <button onClick={() => navigate("/admin/warranties")}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft size={14} /> Warranties
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900"
            data-testid={WARR.detailHeader}>
            <span className="font-mono">{detail.certificate_no || "Warranty"}</span>
          </h1>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <span data-testid={WARR.detailType}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[detail.warranty_type]}`}>
              {detail.warranty_type}
            </span>
            <span data-testid={WARR.detailStatus}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[detail.effective_status]}`}>
              {detail.effective_status}
            </span>
            <span data-testid={WARR.detailPlan} className="text-xs">
              · {detail.plan_label} ({detail.duration_days} days)
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {canWrite && !isVoided && (
            <>
              {detail.certificate_no ? (
                <button data-testid={WARR.certificateDownload} onClick={downloadCert}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                  <Download size={14} /> {detail.certificate_no}
                </button>
              ) : (
                <button data-testid={WARR.certificateBtn} onClick={issueCert} disabled={busy}
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
                  <Award size={14} /> Issue certificate
                </button>
              )}
              <button data-testid={WARR.renewBtn} onClick={() => setModal("renew")}
                className="inline-flex items-center gap-1 rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800">
                <RefreshCw size={14} /> Renew
              </button>
            </>
          )}
          {canVoid && !isVoided && (
            <button data-testid={WARR.voidBtn} onClick={() => setModal("void")}
              className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-white px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-50">
              <Ban size={14} /> Void
            </button>
          )}
        </div>
      </div>

      {actionErr && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionErr}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-2">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Customer</div>
          <div className="text-slate-900 font-medium">{detail.customer_name || "—"}</div>
          <div className="text-xs text-slate-500 font-mono">{detail.mobile}</div>
          {detail.customer_email && <div className="text-xs text-slate-500">{detail.customer_email}</div>}
          <div className="pt-2 mt-2 border-t border-slate-100 text-xs text-slate-500">
            <div>SO: <span className="font-mono">{detail.sales_order_no || "—"}</span></div>
            <div>Invoice: <span className="font-mono">{detail.invoice_no || "—"}</span></div>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-2">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Laptop</div>
          <div className="text-slate-900 font-medium">
            {detail.brand} {detail.model}
          </div>
          <div className="text-xs text-slate-500 font-mono">{detail.passport_barcode}</div>
          <div className="text-xs text-slate-500 font-mono">SN: {detail.serial_number || detail.passport_serial}</div>
        </div>
        <div data-testid={WARR.detailPeriod}
          className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-2">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1">
            {isActive ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
            Coverage
          </div>
          <div className="text-slate-900 font-medium">{detail.starts_on} → {detail.ends_on}</div>
          <div className="text-xs text-slate-500">
            {isActive
              ? <span className="text-emerald-700">{detail.days_left} days remaining</span>
              : (isVoided
                  ? <span className="text-rose-700">Voided{detail.voided_reason ? `: ${detail.voided_reason}` : ""}</span>
                  : <span>Expired</span>)}
          </div>
          {detail.amount_paid != null && (
            <div className="text-xs text-slate-500">Paid: ₹{detail.amount_paid}</div>
          )}
          {detail.parent_warranty_id && (
            <div className="pt-2 mt-2 border-t border-slate-100 text-xs text-slate-500">
              Renewed from <span className="font-mono">{detail.parent_warranty_type}</span> warranty
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">
            Warranty history for this laptop ({history.length})
          </div>
          <table className="w-full text-sm" data-testid={WARR.historyTable}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Plan</th>
                <th className="text-left px-4 py-2">Period</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Invoice</th>
                <th className="text-left px-4 py-2">Certificate</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} data-testid={WARR.historyRow(h.id)}
                  onClick={() => navigate(`/admin/warranties/${h.id}`)}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${h.id === detail.id ? "bg-slate-100" : ""}`}>
                  <td className="px-4 py-2 text-xs">
                    <Chip text={h.warranty_type} cls={TYPE_COLORS[h.warranty_type]} />
                  </td>
                  <td className="px-4 py-2 text-xs">{h.plan_label} ({h.plan_code})</td>
                  <td className="px-4 py-2 text-xs">{h.starts_on} → {h.ends_on}</td>
                  <td className="px-4 py-2 text-xs">
                    <Chip text={h.effective_status} cls={STATUS_COLORS[h.effective_status]} />
                  </td>
                  <td className="px-4 py-2 text-xs font-mono">{h.invoice_no || "—"}</td>
                  <td className="px-4 py-2 text-xs font-mono">{h.certificate_no || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Renew modal */}
      <Modal title="Renew warranty" open={modal === "renew"} onClose={closeModal}>
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Plan</span>
            <select data-testid={WARR.renewPlan}
              value={renewPlanId} onChange={(e) => setRenewPlanId(e.target.value)}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">— same plan as this warranty —</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label} ({p.code}, {p.duration_days} days · ₹{p.base_price})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Amount paid</span>
            <input type="number" min={0} step="0.01"
              data-testid={WARR.renewAmount}
              value={renewAmount} onChange={(e) => setRenewAmount(e.target.value)}
              placeholder="Leave empty to record ₹0"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="text-xs text-slate-500">
            The new warranty starts the day after this one ends (or today if
            already expired) and is linked as a renewal.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={WARR.renewSubmit} disabled={busy}
              onClick={submitRenew}
              className="rounded-md bg-teal-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-800 disabled:opacity-40">
              {busy ? "Renewing…" : "Renew"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Void modal */}
      <Modal title="Void warranty" open={modal === "void"} onClose={closeModal}>
        <div className="space-y-3">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Voiding cannot be undone. Renewal chain will remain visible in
            history but this warranty won't count for claim eligibility.
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Reason *</span>
            <textarea data-testid={WARR.voidReason}
              value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={closeModal}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={WARR.voidSubmit} disabled={busy || !voidReason.trim()}
              onClick={submitVoid}
              className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40">
              {busy ? "Voiding…" : "Void"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}
